import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Image } from 'react-bootstrap';
import { FaEdit, FaTrash, FaEye, FaRegClock } from 'react-icons/fa';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Products.css';

const SellerProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get(`/products/${id}/`);
        setProduct(response.data);
      } catch (err) {
        setError('Failed to fetch product details.');
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/products/${product.id}/`);
      setSuccess('Product deleted successfully.');
      setTimeout(() => navigate('/seller/products'), 1200);
    } catch (err) {
      setError('Failed to delete product.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!product) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const newStatus = product.status === 'active' ? 'hidden' : 'active';
      await api.patch(`/products/${product.id}/`, { status: newStatus });
      setProduct({ ...product, status: newStatus });
      setSuccess(`Product status changed to ${newStatus}.`);
    } catch (err) {
      setError('Failed to update product status.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <Layout>
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    </Layout>
  );

  if (error) return (
    <Layout>
      <Container className="py-5 text-center">
        <Alert variant="danger">{error}</Alert>
      </Container>
    </Layout>
  );

  // Frontend ownership check
  if (product && currentUser && product.seller?.id !== currentUser.id) {
    return (
      <Layout>
        <Container className="py-5 text-center">
          <Alert variant="danger">You do not have permission to view this product.</Alert>
        </Container>
      </Layout>
    );
  }

  if (!product) return null;

  const isApprovalBlocked = product.approval_status && product.approval_status !== 'approved';

  return (
    <Layout>
      <Container className="py-4">
        {product.approval_status === 'pending' && (
          <Alert variant="warning">
            This product is pending admin approval. It won’t appear on the marketplace until it is approved.
          </Alert>
        )}
        {product.approval_status === 'rejected' && (
          <Alert variant="danger">
            This product was rejected by an admin. Please edit the listing and resubmit.
          </Alert>
        )}
        <Row className="mb-4 align-items-center">
          <Col md={8}>
            <h2>{product.name || 'Untitled Product'}</h2>
            <Badge bg={product.status === 'active' ? 'success' : 'secondary'} className="ms-2">
              {product.status || 'Unknown'}
            </Badge>
            {product.approval_status && (
              <Badge
                bg={product.approval_status === 'approved' ? 'success' : product.approval_status === 'rejected' ? 'danger' : 'warning'}
                text={product.approval_status === 'pending' ? 'dark' : undefined}
                className="ms-2"
              >
                {product.approval_status || 'Unknown'}
              </Badge>
            )}
          </Col>
          <Col md={4} className="text-end">
            <Button as={Link} to={`/seller/edit-product/${product.id}`} variant="outline-primary" className="me-2">
              <FaEdit /> Edit
            </Button>
            <Button
              variant={product.status === 'active' ? 'warning' : 'success'}
              className="me-2"
              onClick={handleToggleStatus}
              disabled={isApprovalBlocked}
            >
              <FaEye /> {product.status === 'active' ? 'Hide' : 'Activate'}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <FaTrash /> Delete
            </Button>
          </Col>
        </Row>
        {success && <Alert variant="success">{success}</Alert>}
        <Card className="mb-4">
          <Row className="g-0">
            <Col md={5} className="p-3">
              {product.images && product.images.length > 0 ? (
                <Image src={product.images[0].image} fluid rounded />
              ) : (
                <div className="bg-light d-flex align-items-center justify-content-center" style={{height: '300px'}}>
                  <span>No Image</span>
                </div>
              )}
            </Col>
            <Col md={7} className="p-4">
              <h4>Details</h4>
              <p><strong>Price:</strong> ₦{product.price || '0'}</p>
              <p><strong>Stock:</strong> {product.stockCount || '0'}</p>
              <p><strong>Category:</strong> {product.category || 'Uncategorized'}</p>
              <p><strong>Condition:</strong> {product.condition || 'Unknown'}</p>
              <p><strong>Description:</strong></p>
              <div dangerouslySetInnerHTML={{ __html: product.description || '<p>No description available</p>' }} />
              <p className="text-muted mt-3"><FaRegClock /> Listed on: {product.createdAt ? new Date(product.createdAt).toLocaleString() : 'Unknown date'}</p>
              <h5 className="mt-4">Key Features</h5>
              <ul>
                {product.features && product.features.length > 0 ? (
                  product.features.map((feature, idx) => <li key={idx}>{feature}</li>)
                ) : <li>No features listed.</li>}
              </ul>
            </Col>
          </Row>
        </Card>
      </Container>
    </Layout>
  );
};

export default SellerProductDetail;
